/**
 * Inventory (07) data layer — the ONLY place this feature talks to the server. Every path + capability
 * below is code-verified against server/src/modules/inventory/{routes,controller,validators}.js. Server
 * data lives only in TanStack Query; mutations invalidate the affected lab-scoped keys.
 *
 * Endpoints consumed (all under /api/v1):
 *   GET   /inventory/reactifs                       inventory.reactif.read
 *   POST  /inventory/reactifs                       inventory.reactif.create
 *   PATCH /inventory/reactifs/:id                   inventory.reactif.update
 *   GET   /inventory/reactifs/:id/reorder           inventory.reactif.read
 *   GET   /inventory/reactifs/:id/risk              inventory.reactif.read
 *   GET   /inventory/lots                           inventory.lot.read     (NOVA-027 — all lifecycle states)
 *   GET   /inventory/lots/:id/mouvements            inventory.lot.read     (NOVA-058 — per-lot ledger history)
 *   POST  /inventory/lots                           inventory.lot.receive
 *   POST  /inventory/lots/:id/statut                inventory.mouvement.create
 *   POST  /inventory/sweep                          inventory.mouvement.create
 *   POST  /inventory/recettes                       inventory.recette.manage
 *   POST  /inventory/charges                        inventory.charge.set
 *   POST  /inventory/lots/:id/recall/preview        inventory.recall.run
 *   POST  /inventory/lots/:id/recall/confirm        inventory.recall.run
 *
 * The FEFO `risk` query is actif-only; the recall picker uses GET /inventory/lots (NOVA-027) to also reach
 * quarantaine/perime/epuise lots. The catalog list remains the join root for the dashboards.
 */
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type { ApiError } from "@/api";
import type {
  ChargeResult,
  Id,
  InventaireScope,
  Lot,
  LotMovements,
  LotReceived,
  LotStatut,
  LotStatutResult,
  PostInventaireResult,
  RecallConfirm,
  RecallPreview,
  RecetteCreated,
  Reactif,
  Reorder,
  RiskLot,
  SessionInventaire,
  SweepResult,
  UniteMesure,
} from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the inventory feature. */
export const inventoryKeys = {
  reactifs: (labId: string | null) => qk.scoped(labId, "inventory", "reactifs"),
  reorder: (labId: string | null, reactifId: string) =>
    qk.scoped(labId, "inventory", "reorder", reactifId),
  risk: (labId: string | null, reactifId: string) =>
    qk.scoped(labId, "inventory", "risk", reactifId),
  lots: (labId: string | null, reactifId: string) =>
    qk.scoped(labId, "inventory", "lots", reactifId),
  lotMovements: (labId: string | null, lotId: string) =>
    qk.scoped(labId, "inventory", "lot-movements", lotId),
  inventaire: (labId: string | null, sessionId: string) =>
    qk.scoped(labId, "inventory", "inventaire", sessionId),
};

// ── Queries ──────────────────────────────────────────────────────────────────────

/** The reagent catalog for the tenant. The join root for reorder / risk / charge / recall. */
export function useReactifs(labId: string | null, enabled: boolean) {
  return useApiQuery<Reactif[]>(inventoryKeys.reactifs(labId), "/inventory/reactifs", {
    enabled,
    staleTime: 30_000,
  });
}

/** Reorder bucket (OK|BAS|RUPTURE) for one reagent. */
export function useReorder(labId: string | null, reactifId: string | null, enabled: boolean) {
  return useApiQuery<Reorder>(
    inventoryKeys.reorder(labId, reactifId ?? "_"),
    `/inventory/reactifs/${reactifId ?? "_"}/reorder`,
    { enabled: enabled && !!reactifId, staleTime: 15_000 },
  );
}

/** One reorder row resolved for the dashboard (reagent meta joined with its bucket). */
export interface ReorderRow {
  reactif: Reactif;
  reorder: Reorder | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Batched reorder — one GET /reactifs/:id/reorder per reagent, fanned out via useQueries (a single
 * fixed hook call, so the rules-of-hooks hold regardless of catalog size). The dashboard joins each
 * result back to its reagent. Each query is independently cached + invalidatable by the lot mutations.
 */
export function useReorderBatch(
  labId: string | null,
  reactifs: ReadonlyArray<Reactif>,
  enabled: boolean,
): ReorderRow[] {
  const results = useQueries({
    queries: reactifs.map((r) => {
      const id = idStrLocal(r.id);
      return {
        queryKey: inventoryKeys.reorder(labId, id),
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          api.get<Reorder>(`/inventory/reactifs/${id}/reorder`, undefined, signal),
        enabled: enabled && !!id,
        staleTime: 15_000,
      };
    }),
  });
  return reactifs.map((reactif, i) => {
    const q = results[i] as
      | { data?: Reorder; isLoading: boolean; isError: boolean; error: ApiError | null }
      | undefined;
    return {
      reactif,
      reorder: q?.data ?? null,
      isLoading: q?.isLoading ?? false,
      isError: q?.isError ?? false,
    };
  });
}

/** Local id→string (kept private so api.ts has no import cycle with types' idStr re-use elsewhere). */
function idStrLocal(id: Id | null | undefined): string {
  return id == null ? "" : String(id);
}

/** Near-expiry risk tiering (FEFO-ordered lots) for one reagent. */
export function useRisk(labId: string | null, reactifId: string | null, enabled: boolean) {
  return useApiQuery<RiskLot[]>(
    inventoryKeys.risk(labId, reactifId ?? "_"),
    `/inventory/reactifs/${reactifId ?? "_"}/risk`,
    { enabled: enabled && !!reactifId, staleTime: 15_000 },
  );
}

/**
 * NOVA-027 — lots across ALL lifecycle states for one reagent (recall picker). Unlike `useRisk` this also
 * lists quarantaine/perime/epuise lots, so a Magasinier can recall an expired/exhausted lot. The server
 * filters by reactif_id; the recall confirm flips statut so the key is invalidated by useConfirmRecall.
 */
export function useLots(labId: string | null, reactifId: string | null, enabled: boolean) {
  return useApiQuery<Lot[]>(
    inventoryKeys.lots(labId, reactifId ?? "_"),
    "/inventory/lots",
    { enabled: enabled && !!reactifId, staleTime: 15_000, ...(reactifId ? { query: { reactif_id: reactifId } } : {}) },
  );
}

/**
 * NOVA-058 — the per-lot append-only movement history (GET /inventory/lots/:id/mouvements). Read-only:
 * the MouvementStock ledger in insert order + a running balance recomputed server-side. Gated by
 * inventory.lot.read (same as the lots list). Opened from the lots drawer per lot.
 */
export function useLotMovements(labId: string | null, lotId: string | null, enabled: boolean) {
  return useApiQuery<LotMovements>(
    inventoryKeys.lotMovements(labId, lotId ?? "_"),
    `/inventory/lots/${lotId ?? "_"}/mouvements`,
    { enabled: enabled && !!lotId, staleTime: 10_000 },
  );
}

// ── Reactif CRUD mutations ─────────────────────────────────────────────────────────

export interface CreateReactifVars {
  code: string;
  libelle: string;
  discipline_code: string;
  automate_id?: Id;
  unite_mesure?: UniteMesure;
  tests_par_lot?: number;
  seuil_alerte?: number;
}

/** POST /inventory/reactifs — create a reagent. Refreshes the catalog. */
export function useCreateReactif(labId: string | null) {
  return useApiMutation<{ reactif_id: Id; code: string }, CreateReactifVars>(
    (vars) => api.post("/inventory/reactifs", vars),
    { invalidate: [inventoryKeys.reactifs(labId)] },
  );
}

export interface UpdateReactifVars {
  id: string;
  libelle?: string;
  seuil_alerte?: number;
  statut?: "actif" | "inactif";
}

/** PATCH /inventory/reactifs/:id — edit libellé / seuil / statut (full-CRUD, L-1). */
export function useUpdateReactif(labId: string | null) {
  return useApiMutation<{ reactif_id: Id }, UpdateReactifVars>(
    ({ id, ...patch }) => api.patch(`/inventory/reactifs/${id}`, patch),
    { invalidate: [inventoryKeys.reactifs(labId)] },
  );
}

// ── Lot reception + lifecycle ───────────────────────────────────────────────────────

export interface ReceiveLotVars {
  reactif_id: Id;
  numero_lot: string;
  date_peremption: string;
  quantite: number;
  prix_achat?: number;
}

/** POST /inventory/lots — receive a lot (opening `entree` posted server-side). Refreshes reorder+risk. */
export function useReceiveLot(labId: string | null, reactifId: string | null) {
  return useApiMutation<LotReceived, ReceiveLotVars>(
    (vars) => api.post<LotReceived>("/inventory/lots", vars),
    {
      invalidate: reactifId
        ? [
            inventoryKeys.risk(labId, reactifId),
            inventoryKeys.reorder(labId, reactifId),
            inventoryKeys.reactifs(labId),
          ]
        : [inventoryKeys.reactifs(labId)],
    },
  );
}

export interface SetLotStatutVars {
  lotId: string;
  statut: "actif" | "quarantaine";
  motif: string;
  /** For cache invalidation only (not sent). */
  reactifId?: string;
}

/** POST /inventory/lots/:id/statut — quarantine / release a lot (reversible, audited). */
export function useSetLotStatut(labId: string | null) {
  return useApiMutation<LotStatutResult, SetLotStatutVars>(
    ({ lotId, statut, motif }) =>
      api.post<LotStatutResult>(`/inventory/lots/${lotId}/statut`, { statut, motif }),
    {
      // The reagent of the lot is passed via the mutate() options invalidate when known; default to
      // a broad catalog refresh (risk/reorder for the active reagent is invalidated by the caller).
      invalidate: [inventoryKeys.reactifs(labId)],
    },
  );
}

export interface SweepVars {
  as_of?: string;
}

/** POST /inventory/sweep — idempotent expiry sweep. Refreshes the whole catalog (buckets may flip). */
export function useSweep(labId: string | null) {
  return useApiMutation<SweepResult, SweepVars>(
    (vars) => api.post<SweepResult>("/inventory/sweep", vars.as_of ? { as_of: vars.as_of } : {}),
    { invalidate: [inventoryKeys.reactifs(labId)] },
  );
}

// ── Recette (consumption recipe) ─────────────────────────────────────────────────────

export interface CreateRecetteVars {
  analyse_code: string;
  reactif_id: Id;
  automate_id?: Id;
  methode?: string;
  quantite_par_test: number;
  quantite_par_controle?: number;
}

/** POST /inventory/recettes — declare a consumption recipe. No server list → no invalidation. */
export function useCreateRecette() {
  return useApiMutation<RecetteCreated, CreateRecetteVars>((vars) => {
    const body: Record<string, unknown> = {
      analyse_code: vars.analyse_code,
      reactif_id: vars.reactif_id,
      quantite_par_test: vars.quantite_par_test,
    };
    if (vars.automate_id !== undefined && vars.automate_id !== "") body.automate_id = vars.automate_id;
    if (vars.methode) body.methode = vars.methode;
    if (vars.quantite_par_controle !== undefined)
      body.quantite_par_controle = vars.quantite_par_controle;
    return api.post<RecetteCreated>("/inventory/recettes", body);
  });
}

// ── Charge (active lot on analyzer) ──────────────────────────────────────────────────

export interface SetChargeVars {
  automate_id: Id;
  reactif_id: Id;
  lot_reactif_id: Id;
  discipline_code?: string;
}

/** POST /inventory/charges — load an active lot on an analyzer (decharges the prior in the same tx). */
export function useSetCharge() {
  return useApiMutation<ChargeResult, SetChargeVars>((vars) => {
    const body: Record<string, unknown> = {
      automate_id: vars.automate_id,
      reactif_id: vars.reactif_id,
      lot_reactif_id: vars.lot_reactif_id,
    };
    if (vars.discipline_code) body.discipline_code = vars.discipline_code;
    return api.post<ChargeResult>("/inventory/charges", body);
  });
}

// ── Lot → Resultat recall ─────────────────────────────────────────────────────────────

/** POST /inventory/lots/:id/recall/preview — dry-run, NO write. Imperative (drives a confirm step). */
export function previewRecall(lotId: string): Promise<RecallPreview> {
  return api.post<RecallPreview>(`/inventory/lots/${lotId}/recall/preview`, {});
}

export interface ConfirmRecallVars {
  lotId: string;
  motif: string;
  reactifId?: string;
}

/** POST /inventory/lots/:id/recall/confirm — flip lot → quarantaine + build the biologiste worklist. */
export function useConfirmRecall(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<RecallConfirm, ConfirmRecallVars>(
    ({ lotId, motif }) => api.post<RecallConfirm>(`/inventory/lots/${lotId}/recall/confirm`, { motif }),
    {
      invalidate: [inventoryKeys.reactifs(labId)],
      // Also refresh the reagent's lots list (when known) so the recall picker reflects the new
      // quarantaine state on re-preview (NOVA-027 — the post-confirm re-preview stays correct).
      onSuccess: (_data, vars) => {
        if (vars?.reactifId) void qc.invalidateQueries({ queryKey: inventoryKeys.lots(labId, vars.reactifId) });
      },
    },
  );
}

// ── Cycle-count (INV-8 — NOVA-024) ─────────────────────────────────────────────────────
//
// The lifecycle: open (en_cours) → count each ligne → review (en_revue) → post (postee) | cancel (annulee).
// post appends ONE signed ajustement per varying lot (ecart ≠ 0) via the ledger; there is no un-post.
// All five endpoints are gated inventory.inventaire.run.

export interface OpenInventaireVars {
  libelle: string;
  scope?: InventaireScope;
  discipline_code?: string;
  reactif_id?: Id;
}

/** POST /inventory/inventaires — open a cycle-count (snapshots in-scope actif lots into frozen lignes). */
export function useOpenInventaire(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<SessionInventaire, OpenInventaireVars>(
    (vars) => {
      const body: Record<string, unknown> = { libelle: vars.libelle };
      if (vars.scope) body.scope = vars.scope;
      if (vars.discipline_code) body.discipline_code = vars.discipline_code;
      if (vars.reactif_id !== undefined && vars.reactif_id !== "") body.reactif_id = vars.reactif_id;
      return api.post<SessionInventaire>("/inventory/inventaires", body);
    },
    {
      onSuccess: (s) => {
        if (s?.session_id) qc.setQueryData(inventoryKeys.inventaire(labId, s.session_id), s);
      },
    },
  );
}

/** GET /inventory/inventaires/:id — read a session (with its lignes). */
export function useInventaire(labId: string | null, sessionId: string | null, enabled: boolean) {
  return useApiQuery<SessionInventaire>(
    inventoryKeys.inventaire(labId, sessionId ?? "_"),
    `/inventory/inventaires/${sessionId ?? "_"}`,
    { enabled: enabled && !!sessionId, staleTime: 5_000 },
  );
}

export interface CountInventaireVars {
  sessionId: string;
  ligne_id: Id;
  quantite_comptee: number;
}

/** POST /inventory/inventaires/:id/count — record a physical count on a ligne (ecart = comptee − systeme). */
export function useCountInventaire(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<SessionInventaire, CountInventaireVars>(
    ({ sessionId, ligne_id, quantite_comptee }) =>
      api.post<SessionInventaire>(`/inventory/inventaires/${sessionId}/count`, { ligne_id, quantite_comptee }),
    {
      onSuccess: (s) => {
        if (s?.session_id) qc.setQueryData(inventoryKeys.inventaire(labId, s.session_id), s);
      },
    },
  );
}

/** POST /inventory/inventaires/:id/review — move the count to en_revue (all counts in, awaiting post). */
export function useReviewInventaire(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<SessionInventaire, { sessionId: string }>(
    ({ sessionId }) => api.post<SessionInventaire>(`/inventory/inventaires/${sessionId}/review`, {}),
    {
      onSuccess: (s) => {
        if (s?.session_id) qc.setQueryData(inventoryKeys.inventaire(labId, s.session_id), s);
      },
    },
  );
}

/**
 * POST /inventory/inventaires/:id/post — post the count: one signed ajustement per varying lot via the
 * ledger. Idempotent (re-post is a no-op). Refreshes the session + the catalog (on-hand buckets flip).
 */
export function usePostInventaire(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<PostInventaireResult, { sessionId: string }>(
    ({ sessionId }) => api.post<PostInventaireResult>(`/inventory/inventaires/${sessionId}/post`, {}),
    {
      invalidate: [inventoryKeys.reactifs(labId)],
      onSuccess: (res) => {
        if (res?.session_id) void qc.invalidateQueries({ queryKey: inventoryKeys.inventaire(labId, res.session_id) });
      },
    },
  );
}

/** POST /inventory/inventaires/:id/cancel — abort a not-yet-postee count (idempotent on annulee). */
export function useCancelInventaire(labId: string | null) {
  const qc = useQueryClient();
  return useApiMutation<SessionInventaire, { sessionId: string }>(
    ({ sessionId }) => api.post<SessionInventaire>(`/inventory/inventaires/${sessionId}/cancel`, {}),
    {
      onSuccess: (s) => {
        if (s?.session_id) qc.setQueryData(inventoryKeys.inventaire(labId, s.session_id), s);
      },
    },
  );
}

/** Re-export so screens can branch lifecycle display without importing types twice. */
export type { LotStatut };
